<script lang="ts">
  import type { Corner } from "./autoDetect";
  import { onMount } from "svelte";
  import { lockBodyScroll } from "../utils/scrollLock";

  interface Props {
    imageBlob: Blob;
    initialCorners?: Corner[];
    onConfirm: (corners: Corner[]) => void;
    onUseOriginal?: () => void;
    onCancel: () => void;
  }
  const { imageBlob, initialCorners, onConfirm, onUseOriginal, onCancel }: Props = $props();

  let imgUrl = $state("");
  let imgEl = $state<HTMLImageElement>();
  let svgEl = $state<SVGSVGElement>();
  let corners = $state<Corner[]>([]); // image-space pixel coords
  let displayScale = $state(1);
  let imageBox = $state({ width: 0, height: 0 });
  let dragging = $state<number | null>(null);

  onMount(() => {
    const unlock = lockBodyScroll();
    imgUrl = URL.createObjectURL(imageBlob);
    return () => {
      URL.revokeObjectURL(imgUrl);
      unlock();
    };
  });

  function onImageLoad() {
    if (!imgEl) return;
    const w = imgEl.naturalWidth;
    const h = imgEl.naturalHeight;
    if (initialCorners && initialCorners.length === 4) {
      corners = initialCorners;
    } else {
      const inset = Math.min(w, h) * 0.1;
      corners = [
        { x: inset, y: inset },
        { x: w - inset, y: inset },
        { x: w - inset, y: h - inset },
        { x: inset, y: h - inset }
      ];
    }
    updateScale();
  }

  function updateScale() {
    if (!imgEl) return;
    const rect = imgEl.getBoundingClientRect();
    imageBox = { width: rect.width, height: rect.height };
    displayScale = rect.width / imgEl.naturalWidth;
  }

  function onResize() {
    updateScale();
  }

  function pointerDown(i: number) {
    return (e: PointerEvent) => {
      e.preventDefault();
      svgEl?.setPointerCapture(e.pointerId);
      dragging = i;
      pointerMove(e);
    };
  }

  function pointerMove(e: PointerEvent) {
    if (dragging === null || !imgEl) return;
    const point = eventToImagePoint(e);
    if (!point) return;
    const clamped = {
      x: Math.max(0, Math.min(imgEl.naturalWidth, point.x)),
      y: Math.max(0, Math.min(imgEl.naturalHeight, point.y))
    };
    corners = corners.map((c, idx) => (idx === dragging ? clamped : c));
  }

  function pointerUp(e: PointerEvent) {
    if (dragging !== null) {
      if (svgEl?.hasPointerCapture(e.pointerId)) svgEl.releasePointerCapture(e.pointerId);
      dragging = null;
    }
  }

  function eventToImagePoint(e: PointerEvent): Corner | null {
    if (!svgEl) return null;
    const matrix = svgEl.getScreenCTM();
    if (!matrix) return null;
    const point = svgEl.createSVGPoint();
    point.x = e.clientX;
    point.y = e.clientY;
    const transformed = point.matrixTransform(matrix.inverse());
    return { x: transformed.x, y: transformed.y };
  }
</script>

<svelte:window on:resize={onResize} />

<div class="editor">
  <div class="stage">
    <div
      class="image-frame"
      class:measured={imageBox.width > 0}
      style={imageBox.width > 0 ? `width:${imageBox.width}px;height:${imageBox.height}px` : undefined}
    >
      <img bind:this={imgEl} src={imgUrl} alt="" onload={onImageLoad} />
    {#if corners.length === 4}
      <svg
        bind:this={svgEl}
        class="overlay"
        viewBox={`0 0 ${imgEl?.naturalWidth ?? 1} ${imgEl?.naturalHeight ?? 1}`}
        preserveAspectRatio="none"
        role="application"
        aria-label="Document corner editor"
        onpointermove={pointerMove}
        onpointerup={pointerUp}
        onpointercancel={pointerUp}
      >
        <polygon
          points={corners.map((c) => `${c.x},${c.y}`).join(" ")}
          fill="rgba(242,215,123,0.16)"
          stroke="#f2d77b"
          stroke-width={4 / displayScale}
        />
        {#each corners as c, i}
          <circle
            cx={c.x}
            cy={c.y}
            r={20 / displayScale}
            fill="#071018"
            stroke="#f2d77b"
            stroke-width={4 / displayScale}
            role="button"
            tabindex="0"
            aria-label={`Corner ${i + 1}`}
            onpointerdown={pointerDown(i)}
          />
        {/each}
      </svg>
    {/if}
    </div>
  </div>
  <div class="hint">Drag the gold points to frame the paper</div>
  <div class="controls">
    <button type="button" onclick={onCancel}>Retake</button>
    {#if onUseOriginal}
      <button type="button" onclick={onUseOriginal}>Add original</button>
    {/if}
    <button type="button" class="primary" onclick={() => onConfirm(corners)}>Use</button>
  </div>
</div>

<style>
  .editor {
    position: fixed;
    inset: 0;
    background:
      radial-gradient(circle at 50% 20%, rgb(92 242 255 / 0.18), transparent 22rem),
      linear-gradient(145deg, #04070a, #111923 60%, #050607);
    display: flex;
    flex-direction: column;
  }
  .stage {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 1rem;
  }
  .image-frame {
    position: relative;
    display: flex;
    max-width: 100%;
    max-height: 100%;
    border-radius: 1.35rem;
    overflow: hidden;
    box-shadow: 0 24px 80px rgb(0 0 0 / 0.55), 0 0 0 1px rgb(255 255 255 / 0.12);
  }
  img {
    max-width: 100%;
    max-height: 100%;
    display: block;
  }
  .measured img {
    width: 100%;
    height: 100%;
  }
  .overlay {
    position: absolute;
    inset: 0;
    touch-action: none;
  }
  .controls {
    display: flex;
    gap: 0.65rem;
    padding: 1rem;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0));
    background: linear-gradient(180deg, transparent, rgb(0 0 0 / 0.86) 34%, #000);
  }
  button {
    flex: 1;
    padding: 0.9rem 0.65rem;
    font: inherit;
    font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-weight: 800;
    background: rgb(255 255 255 / 0.08);
    color: #fff;
    border: 1px solid rgb(255 255 255 / 0.18);
    border-radius: 999px;
    backdrop-filter: blur(18px);
  }
  .primary {
    background: #f2d77b;
    color: #071018;
    border-color: #f2d77b;
    box-shadow: 0 12px 36px rgb(242 215 123 / 0.22);
  }
  .hint {
    position: absolute;
    left: 50%;
    bottom: calc(7.25rem + env(safe-area-inset-bottom, 0));
    z-index: 2;
    transform: translateX(-50%);
    white-space: nowrap;
    border: 1px solid rgb(255 255 255 / 0.14);
    border-radius: 999px;
    background: rgb(0 0 0 / 0.52);
    color: rgb(255 255 255 / 0.78);
    padding: 0.45rem 0.75rem;
    font: 700 0.72rem / 1 ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    letter-spacing: 0.04em;
    backdrop-filter: blur(14px);
  }

  @media (max-height: 680px) {
    .hint {
      bottom: calc(6.25rem + env(safe-area-inset-bottom, 0));
      font-size: 0.66rem;
    }
  }
</style>
